import {locales} from '../../sampleLocations';

const createIntent = (country) => {
  const currency = locales[country].currency;
  const pmTypes =
    country === 'CA'
      ? `[@"card_present", @"interac_present"]`
      : `[@"card_present"]`;
  return `
    SCPPaymentIntentParameters *paymentIntentParams = [[SCPPaymentIntentParameters alloc] initWithAmount:1000
                                                                                          currency:@"${currency}"]
                                                                                          paymentMethodTypes:@${pmTypes}];
`;
};

const collectPaymentIP = `- (void)collectPayment {
  {+create-intent}
  [[APIClient shared] createPaymentIntent:^(NSString * _Nullable client_secret, NSError * _Nullable createError) {
  {/create-intent}
      if (createError) {
          NSLog(@"createPaymentIntent failed: %@", createError);
          self.readerMessageLabel.text = [NSString stringWithFormat:@"Error creating payment: %@", createError.localizedDescription];
      } else if (client_secret) {
          NSLog(@"createPaymentIntent succeeded");
          {+collect-payment-method}
          [SCPTerminal.shared retrievePaymentIntent:client_secret completion:^(SCPPaymentIntent * _Nullable intent, NSError * _Nullable error) {
              [SCPTerminal.shared collectPaymentMethod:intent
                                              delegate:self
                                            completion:^(SCPPaymentIntent * _Nullable collectIntent, NSError * _Nullable collectError) {
          {/collect-payment-method}
                  if (collectError) {
                      NSLog(@"collectPaymentMethod failed: %@", collectError);
                      self.readerMessageLabel.text = [NSString stringWithFormat:@"Error collecting payment: %@", collectError.localizedDescription];
                  } else if (collectIntent) {
                      NSLog(@"collectPaymentMethod succeeded");
                      [self processPayment:collectIntent];
                  }
              }];
          }];
      }
  }];
}`;

const collectPaymentBT = (country) => {
  return `- (void)collectPayment {
  {+create-intent-mobile}
  ${createIntent(country)}
  {/create-intent-mobile}
      if (createError) {
          NSLog(@"createPaymentIntent failed: %@", createError);
          self.readerMessageLabel.text = [NSString stringWithFormat:@"Error creating payment: %@", createError.localizedDescription];
      } else if (intent) {
          NSLog(@"createPaymentIntent succeeded");
          {+collect-payment-method}
          [SCPTerminal.shared collectPaymentMethod:intent
                                          delegate:self
                                        completion:^(SCPPaymentIntent * _Nullable collectIntent, NSError * _Nullable collectError) {
          {/collect-payment-method}
              if (collectError) {
                  NSLog(@"collectPaymentMethod failed: %@", collectError);
                  self.readerMessageLabel.text = [NSString stringWithFormat:@"Error collecting payment: %@", collectError.localizedDescription];
              } else if (collectIntent) {
                  NSLog(@"collectPaymentMethod succeeded");
                  [self processPayment:collectIntent];
              }
          }];
      }
  }];
}`;
};

const collectPayment = (country, reader) => {
  if (reader === 'wp3' || reader === 'm2') {
    return collectPaymentBT(country);
  }
  return collectPaymentIP;
};

const connectInternetReader = `{+connect-reader}
    [[SCPTerminal shared] connectInternetReader:selectedReader
                                     completion:^(SCPReader *reader, NSError *error) {
{/connect-reader}`;

const connectBluetoothReader = `
    // When connecting to a physical reader, your integration should specify either the
    // same location as the last connection (selectedReader.locationId) or a new location
    // of your user's choosing.
    //
    // Since the simulated reader is not associated with a real location, we recommend
    // specifying its existing mock location.
    SCPBluetoothReaderConnectionConfiguration *connectionConfig = [[SCPBluetoothReaderConnectionConfiguration alloc] initWithLocationId:selectedReader.locationId];
    {+connect-reader}
    [[SCPTerminal shared] connectBluetoothReader:selectedReader
                                        delegate:readerDelegate
                                connectionConfig:connectionConfig
                                      completion:^(SCPReader *reader, NSError *error) {
{/connect-reader}`;

const connectReader = (reader) => {
  if (reader === 'wp3' || reader === 'm2') {
    return connectBluetoothReader;
  }
  return connectInternetReader;
};

const bluetoothReaderDelegate = (reader) => {
  if (reader === 'wp3' || reader === 'm2') {
    return `
#pragma mark - SCPBluetoothReaderDelegate - only needed for Bluetooth readers, this is the delegate set during connectBluetoothReader

- (void)reader:(SCPReader *)reader didRequestReaderInput:(SCPReaderInputOptions)inputOptions {
    self.readerMessageLabel.text = [SCPTerminal stringFromReaderInputOptions:inputOptions];
}

- (void)reader:(SCPReader *)reader didRequestReaderDisplayMessage:(SCPReaderDisplayMessage)displayMessage {
    self.readerMessageLabel.text = [SCPTerminal stringFromReaderDisplayMessage:displayMessage];
}

- (void)reader:(nonnull SCPReader *)reader didStartInstallingUpdate:(SCPReaderSoftwareUpdate *)update cancelable:(nullable SCPCancelable *)cancelable {
  // Show UI communicating that a required update has started installing
}

- (void)reader:(nonnull SCPReader *)reader didReportReaderSoftwareUpdateProgress:(float)progress {
  // Update the progress of the install
}

- (void)reader:(nonnull SCPReader *)reader didFinishInstallingUpdate:(nullable SCPReaderSoftwareUpdate *)update error:(nullable NSError *)error {
  // Report success or failure of the update
}

- (void)reader:(nonnull SCPReader *)reader didReportAvailableUpdate:(SCPReaderSoftwareUpdate *)update {
  // An update is available for the connected reader. Show this update in your application.
  // This update can be installed using Terminal.shared.installAvailableUpdate.
}
`;
  }
};

const discoveryMethod = (reader) => {
  if (reader === 'wpe') {
    return 'SCPDiscoveryMethodInternet';
  }
  return 'SCPDiscoveryMethodBluetoothScan';
};

const viewController = (reader) => {
  if (reader === 'wpe') {
    return '@interface ViewController () <SCPDiscoveryDelegate>';
  }
  return '@interface ViewController () <SCPDiscoveryDelegate, SCPBluetoothReaderDelegate>';
};

export default ({keys, server, reader, country}) => {
  return `#import "ViewController.h"
#import "APIClient.h"
#import <StripeTerminal/StripeTerminal.h>

${viewController(reader)}

@property (nonatomic, nullable, strong) SCPCancelable *discoverCancelable;
@property (nonatomic, nullable, strong) SCPCancelable *collectCancelable;
@property (nonatomic, strong) UIButton *nextActionButton;
@property (nonatomic, strong) UILabel *readerMessageLabel;

@end

@implementation ViewController

- (void)viewDidLoad {
    [super viewDidLoad];
    [self setUpInterface];

    // Move this to your App Delegate, in didFinishLaunchingWithOptions.
    {+sdk}
    [SCPTerminal setTokenProvider:APIClient.shared];
    {/sdk}
}

- (void)discoverReaders {
    {+discover-reader}
    'SCPDiscoveryConfiguration *discoveryConfiguration = [[SCPDiscoveryConfiguration alloc] initWithDiscoveryMethod:${discoveryMethod(
      reader,
    )}} simulated:YES];'
    self.discoverCancelable = [SCPTerminal.shared discoverReaders:discoveryConfiguration
                                                          delegate:self
                                                        completion:^(NSError * _Nullable error) {
    {/discover-reader}

        if (error) {
            NSLog(@"discoverReaders failed: %@", error);
            self.readerMessageLabel.text = [NSString stringWithFormat:@"discoverReaders failed: %@", error.localizedDescription];
        } else {
            NSLog(@"discoverReaders succeeded");
            [self.nextActionButton setTitle:@"Make a Payment" forState:UIControlStateNormal];
            [self.nextActionButton removeTarget:self action:@selector(discoverReaders) forControlEvents:UIControlEventTouchUpInside];
            [self.nextActionButton addTarget:self action:@selector(collectPayment) forControlEvents:UIControlEventTouchUpInside];
        }
    }];
}

${collectPayment(country, reader)}

- (void)processPayment:(SCPPaymentIntent *)intent {
    {+process-payment}
    [[SCPTerminal shared] processPayment:intent
                              completion:^(SCPPaymentIntent *processIntent, SCPProcessPaymentError *processError) {
    {/process-payment}
        if (processError) {
            NSLog(@"processPayment failed: %@", processError);
            self.readerMessageLabel.text = [NSString stringWithFormat:@"Error processing payment: %@", processError.localizedDescription];
        } else {
            NSLog(@"processPayment succeeded");

            // Notify your backend to capture the PaymentIntent.
            // PaymentIntents processed with Stripe Terminal must be captured
            // within 24 hours of processing the payment.
            [[APIClient shared] capturePaymentIntent:processIntent.stripeId
                                          completion:^(NSError *captureError) {
                if (captureError) {
                    NSLog(@"capture failed: %@", captureError);
                    self.readerMessageLabel.text = [NSString stringWithFormat:@"Error capturing payment: %@", captureError.localizedDescription];
                }
                else {
                    NSLog(@"capture succeeded");
                    self.readerMessageLabel.text = @"Payment succeeded";
                }
            }];
        }
    }];
}

- (void)setUpInterface {
    self.nextActionButton = [UIButton buttonWithType:UIButtonTypeSystem];;
    self.readerMessageLabel = [[UILabel alloc] init];

    self.readerMessageLabel.textAlignment = NSTextAlignmentCenter;
    self.readerMessageLabel.numberOfLines = 0;

    [self.nextActionButton setTitle:@"Connect to a reader" forState:UIControlStateNormal];
    [self.nextActionButton addTarget:self action:@selector(discoverReaders) forControlEvents:UIControlEventTouchUpInside];

    UIStackView *stackView = [[UIStackView alloc] initWithArrangedSubviews:@[self.nextActionButton, self.readerMessageLabel]];
    stackView.axis = UILayoutConstraintAxisVertical;
    stackView.translatesAutoresizingMaskIntoConstraints = false;
    [self.view addSubview:stackView];
    [NSLayoutConstraint activateConstraints:@[
        [stackView.centerYAnchor constraintEqualToAnchor:self.view.centerYAnchor],
        [stackView.centerXAnchor constraintEqualToAnchor:self.view.centerXAnchor],
        [stackView.leadingAnchor constraintEqualToAnchor:self.view.leadingAnchor],
        [stackView.trailingAnchor constraintEqualToAnchor:self.view.trailingAnchor],
    ]];

}

#pragma mark - SCPDiscoveryDelegate
- (void)terminal:(SCPTerminal *)terminal didUpdateDiscoveredReaders:(NSArray<SCPReader *> *)readers {
    self.readerMessageLabel.text = [NSString stringWithFormat:@"%lu readers found", readers.count];

    // Select the first reader the SDK discovers. In your app,
    // you should display the available readers to your user, then
    // connect to the reader they've selected.
    SCPReader *selectedReader = [readers firstObject];

    // Only connect if we aren't currently connected.
    if (terminal.connectionStatus != SCPConnectionStatusNotConnected) {
        return;
    }

    ${connectReader(reader)}
        if (reader != nil) {
            NSLog(@"Successfully connected to reader: %@", reader);
            self.readerMessageLabel.text = @"Connected to reader.";
        } else {
            NSLog(@"connection failed: %@", error);
            self.readerMessageLabel.text = [NSString stringWithFormat:@"Error connecting: %@", error.localizedDescription];
        }
    }];
}
${bluetoothReaderDelegate(reader)}
@end`;
};
